<script setup lang="ts">
import { computed, useRoute, navigateTo } from '#imports';
import { useMediaQuery } from '@vueuse/core';

import type { TLetters } from '@/entities/Dictionary/types';

import VScroll from '@/shared/components/global/v-scroll.vue';
import IconAngle from '@/assets/icons/icon-angle.svg';

type PaginationType = 'secondary';

const props = withDefaults(defineProps<{
	letters: TLetters;
	dictionary: string | number;
	isOverflow?: boolean;
	variations?: PaginationType[],
}>(), {
	isOverflow: false,
	variations: () => [],
});

const route = useRoute();
const letterSlug = route.params.letter as string;
const isBigScreen = useMediaQuery('(min-width: 1024px)');

const computedClass = computed(() => {
	return props.variations.map((modificator) => {
		return `pagination-letter--${modificator}`;
	});
});

const handleNavigate = (letterSlug: string) => {
	return navigateTo({
		path: `/dictionary/${props.dictionary}/${letterSlug}`,
	});
};

const computedWrapper = computed(() => {
	return props.isOverflow ? VScroll : 'div';
});

const buttonClass = (slug: string) => {
	if (!props.variations.includes('secondary')) return {};

	return {
		'pagination-letter__button--active': letterSlug === slug,
	};
};
</script>

<template>
	<div class="pagination-letter" :class="computedClass">
		<div v-if="isOverflow && !isBigScreen" class="pagination-letter__navigate pagination-letter__navigate--prev">
			<icon-angle class="pagination-letter__navigate-icon icon" />
		</div>

		<component :is="computedWrapper" class="pagination-letter__wrapper">
			<ol class="pagination-letter__list">
				<li
					v-for="item in letters"
					:key="item.id"
					class="pagination-letter__item"
				>
					<button
						class="pagination-letter__button button button--icon"
						:class="buttonClass(item.slug)"
						@click="handleNavigate(item.slug)"
						v-text="item.letter"
					/>
				</li>
			</ol>
		</component>

		<div v-if="isOverflow && !isBigScreen" class="pagination-letter__navigate">
			<icon-angle class="pagination-letter__navigate-icon icon" />
		</div>
	</div>
</template>

<style lang="scss" scoped>
.pagination-letter {
	display: flex;
	justify-content: space-between;

	&--secondary {
		align-items: flex-start;
		gap: rem($gap-mini);

		width: 100%;
		padding-bottom: rem($gap-mini);
	}

	&--secondary & {
		&__list {
			flex-wrap: nowrap;

			padding-bottom: rem(10px);

			@media #{$screen-laptop} {
				margin: 0;
			}
		}

		&__navigate {
			margin-top: rem(3px);
		}
	}

	&__navigate {
		$size: 19px;

		width: rem($size);
		height: rem($size);

		@media #{$screen-desktop} {
			display: none;
		}

		&--prev & {
			&-icon {
				transform: rotate(180deg);
			}
		}

		&-icon {
			$size: 19px;

			width: rem($size);
			height: rem($size);

			color: $malibu;
		}
	}

	&__list {
		display: flex;
		flex-wrap: wrap;

		margin: 0 rem(-6px);

		list-style: none;
	}

	&__button {
		$size: 26px;

		@include text-medium;

		width: rem($size);
		height: rem($size);
		padding: rem(2px) rem(6px);

		text-transform: uppercase;

		color: $malibu;

		@media #{$screen-tablet} {
			@include text-base
		}

		@include hover {
			font-weight: 500;

			text-decoration: underline;

			color: $black;
		}

		&--active {
			font-weight: 500;

			text-decoration: underline;

			color: $black;
		}
	}
}
</style>
